import {HeaderAgentTasks} from '@github-ui/issue-agent-tasks/HeaderAgentTasks'
import {useIsVisualizationEnabled} from '@github-ui/issue-agent-tasks/utils/is-visualization-enabled'
import {Suspense} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {OptionConfig} from '../OptionConfig'
import type {HeaderSubIssueSummary$key} from './__generated__/HeaderSubIssueSummary.graphql'
import type {LinkedSecurityAlertsFragment$key} from './__generated__/LinkedSecurityAlertsFragment.graphql'
import type {StickyHeaderChips$key} from './__generated__/StickyHeaderChips.graphql'
import type {TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import type {HeaderIssueTypeFragment} from './HeaderIssueType'
import {SmallHeaderIssueType} from './HeaderIssueType'
import {HeaderSubIssueSummary} from './HeaderSubIssueSummary'
import {LinkedSecurityAlerts} from './LinkedSecurityAlerts'
import {SmallRepositoryPill} from './RepositoryPill'
import styles from './StickyHeaderChips.module.css'
import {SmallTrackedBy} from './TrackedBy'

export type StickyHeaderChipsProps = {
  issueKey: StickyHeaderChips$key
  secondaryIssueDataKey?: TrackedByFragment$key | HeaderSubIssueSummary$key | LinkedSecurityAlertsFragment$key
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  optionConfig: OptionConfig
}

export function StickyHeaderChips({
  issueKey,
  secondaryIssueDataKey,
  blockedBySecondaryData,
  optionConfig,
}: StickyHeaderChipsProps) {
  const showAgentTasksPanel = useIsVisualizationEnabled()
  const {showRepositoryPill} = optionConfig

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment StickyHeaderChips on Issue {
        url
        repository {
          nameWithOwner
        }
        issueType {
          name
          color
        }
        ...HeaderAgentTasks
        ...HeaderSubIssueSummaryWithPrimary
        ...RepositoryPill
      }
    `,
    issueKey,
  )

  return (
    <div className={styles.chipsRow}>
      {/* RELAY-CLEANUP(StickyHeaderChips): open-enum `color` from the still-Relay parent.
          Remove when StickyHeaderChips leaves Relay. */}
      <SmallHeaderIssueType data={data as HeaderIssueTypeFragment} />
      {showAgentTasksPanel && <HeaderAgentTasks issueData={data} isSticky />}
      <HeaderSubIssueSummary
        subIssuePrimaryKey={data}
        subIssueSecondaryKey={secondaryIssueDataKey as HeaderSubIssueSummary$key | undefined}
        size="small"
      />
      {secondaryIssueDataKey && (
        <>
          <HeaderBlockedBySummary blockedBySecondaryData={blockedBySecondaryData} size="small" />
          <LinkedSecurityAlerts issueKey={secondaryIssueDataKey as LinkedSecurityAlertsFragment$key} isSmall />
          <Suspense>
            <SmallTrackedBy url={data.url} trackedByKey={secondaryIssueDataKey as TrackedByFragment$key} />
          </Suspense>
        </>
      )}
      {showRepositoryPill && <SmallRepositoryPill repositoryPillData={data} />}
    </div>
  )
}
